import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { Stripe, loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements, useElements } from "@stripe/react-stripe-js";
import SiteContext from "./SiteContext";
import TaxContext from "./TaxContext";
import OrderContext from "./OrderContext";

interface PaymentContextType {
  stripe: Stripe | null;
  isInitializing: boolean;
  error: string | null;
  elements: ReturnType<typeof useElements>;
}

const PaymentContext = createContext<PaymentContextType>({
  stripe: null,
  isInitializing: true,
  error: null,
  elements: null,
});

// Create an inner provider that has access to Elements
const InnerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const elements = useElements();
  const value = useContext(PaymentContext);

  return (
    <PaymentContext.Provider
      value={{
        ...value,
        elements,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

interface PaymentProviderProps {
  children: React.ReactNode;
}

export const PaymentContextProvider: React.FC<PaymentProviderProps> = ({
  children,
}) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { stripePublicKey, site } = useContext(SiteContext);
  const { currency } = useContext(TaxContext);
  const { orderSummary } = useContext(OrderContext);

  useEffect(() => {
    const initStripe = async () => {
      if (!stripePublicKey) {
        setError("Stripe public key not available");
        setIsInitializing(false);
        return;
      }

      try {
        const stripeInstance = await loadStripe(stripePublicKey);
        setStripe(stripeInstance);
      } catch (err) {
        setError("Failed to initialize Stripe");
        console.error("Stripe initialization error:", err);
      } finally {
        setIsInitializing(false);
      }
    };

    initStripe().then();
  }, [stripePublicKey]);

  const value = {
    stripe,
    isInitializing,
    error,
    elements: null,
  };

  const stripeOptions = useMemo<StripeElementsOptions | undefined>(() => {
    // If the order total is 0, don't render the Stripe Elements
    if (!orderSummary?.finalPriceIncTax || orderSummary.finalPriceIncTax <= 0) {
      return undefined;
    }

    return {
      appearance: {
        theme: "stripe",
      },
      mode: "payment",
      amount: orderSummary.finalPriceIncTax,
      currency: currency?.stripeCurrency,
      onBehalfOf: site?.onBehalfOf,
    };
  }, [
    orderSummary?.finalPriceIncTax,
    currency?.stripeCurrency,
    site?.onBehalfOf,
  ]);

  return (
    <PaymentContext.Provider value={value}>
      <Elements stripe={stripe} options={stripeOptions}>
        <InnerProvider>{children}</InnerProvider>
      </Elements>
    </PaymentContext.Provider>
  );
};

export default PaymentContext;
